import { useMemo, useRef, useState } from 'react';
import {
  RESALE_WORKFLOW_STEPS,
  isCheckedIn,
} from '../lib/resaleDesk';
import { displayName, formatWhen, statusLabel, vehicleTitle } from '../lib/format';
import { updateBookingStatus } from '../lib/bookingData';
import ResaleInspectionPanel from './ResaleInspectionPanel';

function stepPanelMode(stepId) {
  return (
    {
      details: 'seller',
      inspection: 'inspection',
      costs: 'costs',
      publish: 'publish',
    }[stepId] || null
  );
}

export default function ResaleInspectionWorkbench({
  booking,
  reportId,
  branchId,
  onBookingChange,
  onDeleted,
  onFinalized,
}) {
  const [stepId, setStepId] = useState('arrival');
  const [checkingIn, setCheckingIn] = useState(false);
  const [checkInError, setCheckInError] = useState('');
  const [stepError, setStepError] = useState('');
  const [navigating, setNavigating] = useState(false);
  const [finalized, setFinalized] = useState(false);
  const panelRef = useRef(null);

  const walkIn = !booking;
  const checkedIn = walkIn || isCheckedIn(booking);
  const panelMode = stepPanelMode(stepId);

  const stepIndex = RESALE_WORKFLOW_STEPS.findIndex((step) => step.id === stepId);

  const stepState = useMemo(() => {
    return RESALE_WORKFLOW_STEPS.map((step, index) => {
      if (finalized) return 'done';
      if (step.id === 'arrival' && checkedIn) return 'done';
      if (index < stepIndex) return 'done';
      if (index === stepIndex) return 'current';
      return 'upcoming';
    });
  }, [checkedIn, finalized, stepIndex]);

  async function checkInVehicle() {
    if (!booking || checkedIn) return;
    setCheckingIn(true);
    setCheckInError('');
    const { data, error } = await updateBookingStatus(booking.id, 'checked_in');
    setCheckingIn(false);
    if (error) {
      setCheckInError(error.message || 'Could not check in.');
      return;
    }
    onBookingChange?.({ ...booking, ...data });
    setStepId('details');
  }

  async function saveCurrentStep() {
    if (stepId === 'arrival' || !panelRef.current) return true;
    setStepError('');
    try {
      await panelRef.current.saveDraft({ silent: true });
      return true;
    } catch (err) {
      setStepError(err.message || 'Could not save changes.');
      return false;
    }
  }

  async function goToStep(targetId) {
    if (targetId === stepId) return;
    setNavigating(true);
    try {
      if (stepId !== 'arrival' && targetId !== stepId) {
        const saved = await saveCurrentStep();
        if (!saved) return;
      }
      setStepId(targetId);
    } finally {
      setNavigating(false);
    }
  }

  async function goNext() {
    const next = RESALE_WORKFLOW_STEPS[stepIndex + 1];
    if (next) await goToStep(next.id);
  }

  async function goBack() {
    const prev = RESALE_WORKFLOW_STEPS[stepIndex - 1];
    if (prev) await goToStep(prev.id);
  }

  return (
    <div className="resale-workbench">
      <ol className="resale-flow">
        {RESALE_WORKFLOW_STEPS.map((step, index) => {
          const status = stepState[index];
          const open = step.id === stepId;
          return (
            <li key={step.id} className="resale-flow-item">
              {index > 0 ? <div className="resale-flow-connector" aria-hidden="true" /> : null}
              <section
                className={[
                  'resale-flow-step',
                  open ? 'is-open' : '',
                  status === 'done' ? 'is-done' : '',
                  status === 'current' ? 'is-current' : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
              >
                <button
                  type="button"
                  className="resale-flow-toggle"
                  aria-expanded={open}
                  onClick={() => goToStep(step.id)}
                >
                  <span className="resale-flow-num">{step.number}</span>
                  <span className="resale-flow-copy">
                    <strong>{step.title}</strong>
                    <small>{step.lede}</small>
                  </span>
                  <span className="resale-flow-meta">
                    {status === 'done' ? (
                      <em className="resale-flow-badge done">Done</em>
                    ) : status === 'current' ? (
                      <em className="resale-flow-badge current">Now</em>
                    ) : (
                      <em className="resale-flow-badge">Next</em>
                    )}
                    <span className="material-symbols-outlined resale-flow-chevron">
                      {open ? 'expand_less' : 'expand_more'}
                    </span>
                  </span>
                </button>

                {open ? (
                  <div className="resale-flow-body">
                    {step.id === 'arrival' ? (
                      <div className="resale-arrival">
                        {booking ? (
                          <>
                            <div className="resale-arrival-grid">
                              <div className="resale-arrival-card">
                                <span className="muted">Scheduled</span>
                                <strong>{formatWhen(booking.scheduled_at)}</strong>
                                <p className="muted">{statusLabel(booking.status)}</p>
                              </div>
                              <div className="resale-arrival-card">
                                <span className="muted">Seller</span>
                                <strong>{displayName(booking.profiles) || '—'}</strong>
                                <p className="muted">{booking.profiles?.phone || 'No phone'}</p>
                              </div>
                              <div className="resale-arrival-card">
                                <span className="muted">Vehicle</span>
                                <strong>{vehicleTitle(booking.vehicles) || '—'}</strong>
                                <p className="muted">
                                  {booking.vehicles?.plate_number || booking.vehicles?.vin || '—'}
                                </p>
                              </div>
                              <div className="resale-arrival-card">
                                <span className="muted">Branch</span>
                                <strong>{booking.branches?.name || '—'}</strong>
                                <p className="muted">{booking.branches?.address || ''}</p>
                              </div>
                            </div>
                            {booking.problem_description ? (
                              <div className="resale-arrival-notes">
                                <span className="muted">Seller notes from app</span>
                                <pre>{booking.problem_description}</pre>
                              </div>
                            ) : null}
                            {checkInError ? <p className="error">{checkInError}</p> : null}
                            {!checkedIn ? (
                              <div className="row-actions">
                                <button
                                  type="button"
                                  className="primary"
                                  disabled={checkingIn}
                                  onClick={checkInVehicle}
                                >
                                  {checkingIn ? 'Checking in…' : 'Check in vehicle'}
                                </button>
                              </div>
                            ) : (
                              <p className="ok">
                                Vehicle checked in
                                {booking.checked_in_at
                                  ? ` · ${formatWhen(booking.checked_in_at)}`
                                  : ''}
                                . Continue with seller and vehicle details.
                              </p>
                            )}
                          </>
                        ) : (
                          <div className="resale-arrival-card">
                            <span className="muted">Walk-in inspection</span>
                            <strong>No app booking linked</strong>
                            <p className="muted">
                              This report was started from the desk. Continue with seller and
                              vehicle details.
                            </p>
                          </div>
                        )}
                        <div className="resale-flow-nav">
                          <button
                            type="button"
                            className="primary"
                            disabled={!checkedIn}
                            onClick={goNext}
                          >
                            Continue
                          </button>
                        </div>
                      </div>
                    ) : (
                      <ResaleInspectionPanel
                        ref={panelRef}
                        reportId={reportId}
                        booking={booking}
                        branchId={branchId || booking?.branch_id}
                        panelMode={panelMode}
                        onDeleted={onDeleted}
                        onFinalized={(report) => {
                          setFinalized(true);
                          onFinalized?.(report);
                        }}
                        onReportChange={(report) => {
                          if (report?.status === 'finalized') setFinalized(true);
                        }}
                      />
                    )}

                    {stepError ? <p className="error">{stepError}</p> : null}

                    {step.id !== 'arrival' && step.id !== 'publish' ? (
                      <div className="resale-flow-nav">
                        <button type="button" className="ghost" disabled={navigating} onClick={goBack}>
                          Back
                        </button>
                        <button type="button" className="primary" disabled={navigating} onClick={goNext}>
                          {navigating ? 'Saving…' : 'Continue'}
                        </button>
                      </div>
                    ) : null}

                    {step.id === 'publish' && !finalized ? (
                      <div className="resale-flow-nav">
                        <button type="button" className="ghost" onClick={goBack}>
                          Back
                        </button>
                      </div>
                    ) : null}
                  </div>
                ) : null}
              </section>
            </li>
          );
        })}
      </ol>

      {!checkedIn && booking ? (
        <p className="muted resale-workbench-hint">
          Check the vehicle in on step 1 before completing the certified report.
        </p>
      ) : null}
    </div>
  );
}
